import { cloneDeep } from 'lodash';
import React, { useEffect, useRef } from 'react';
import SliderInput from '@meitu/saas-ui/es/slider-input';
import { Editor } from '@meitu/saas-editor-core/es/index';
import { ImageLayer } from '@meitu/saas-editor-core/es/layers/image-layer';
import { isEmptyEffect } from '@meitu/saas-editor-core/es/utils/layer-kit';
import { IBorderRadiusProps, IImageLayerProps, ILayerProps, LayerType } from '@meitu/saas-editor-core/es/version-types';
import IconFont from '@meitu/xdesign-icon-font';
import LabelAction from '@/components/label-action';
import { useLocaleStore } from '@/i18n/index';
import EffectItem, { EffectItemShowTitle } from '../components/effect-item/effect-item';
import { useActiveData } from '../hook';
import { AutoDragModal } from './auto-drag-model/auto-drag-model';
import BorderRadiusInput from './border-radius-input/border-radius-input';

const areNumbersEqual = (a: number, b: number, c: number, d: number) => {
    const uniqueNumbers = new Set([a, b, c, d]);
    return uniqueNumbers.size === 1;
};

const DefaultBorderRadius = {
    leftBottom: 0,
    leftTop: 0,
    rightBottom: 0,
    rightTop: 0,
    hidden: false,
} as IBorderRadiusProps;

const ImageBorderRadioItem: React.FC<{
    funcStatTrack: Function;
    editor: Editor;
}> = ({ editor }) => {
    const { $t } = useLocaleStore();
    const { activeLayer, setActiveData } = useActiveData();
    const elementRef = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
        return () => {
            AutoDragModal.close();
        };
    }, []);

    if (!activeLayer || activeLayer.layerType !== LayerType.IMAGE_LAYER) return null;

    const updateLayer = (newLayer: ILayerProps) => {
        const layer = cloneDeep(newLayer);
        if (!isEmptyEffect(activeLayer.data as IImageLayerProps)) {
            editor.getInstance(ImageLayer).changeImageEffectAttr(layer, undefined, 'borderRadius');
            setActiveData(layer);
        } else {
            setActiveData(layer);
            editor.getInstance(ImageLayer).updateLayer(layer);
        }
    };

    const addBorderRadius = () => {
        const data = activeLayer.data as IImageLayerProps;
        data.borderRadius = DefaultBorderRadius;
        updateLayer(activeLayer);
    };

    if (!(activeLayer.data as IImageLayerProps).borderRadius) {
        return (
            <EffectItem
                title={$t((v) => v.imageEffectPanel.borderRadiusItem.title)}
                iconName="UpperRightBold"
                noBorderBottom={true}
                handleClick={() => {
                    window._stat_track?.('hb_layers_func_click', {
                        func_id: '2_162',
                    });
                    addBorderRadius();
                }}
            />
        );
    }

    const visibleClick = () => {
        const data = activeLayer.data as IImageLayerProps;
        const borderRadius = data.borderRadius || DefaultBorderRadius;
        data.borderRadius = {
            ...borderRadius,
            hidden: !borderRadius.hidden,
        };
        updateLayer(activeLayer);
        window._stat_track?.('hb_layers_func_click', {
            func_id: '2_162',
        });
    };

    const delClick = () => {
        const data = activeLayer.data as IImageLayerProps;
        data.borderRadius = null;
        updateLayer(activeLayer);
        AutoDragModal.close();
        window._stat_track?.('hb_layers_func_click', {
            func_id: '2_162',
        });
        window._stat_track?.('hb_layers_func_use', {
            func_id: '2_162',
        });
    };

    const { data } = activeLayer;
    const { borderRadius } = data as IImageLayerProps;

    let borderRadiusTotal = 0;
    if (borderRadius) {
        const { leftBottom, leftTop, rightBottom, rightTop } = borderRadius;
        if (areNumbersEqual(leftBottom, leftTop, rightBottom, rightTop)) {
            borderRadiusTotal = leftBottom;
        } else {
            borderRadiusTotal = -1;
        }
    } else {
        borderRadiusTotal = 0;
    }

    const showRadius = () => {
        const rect = elementRef.current?.getBoundingClientRect();
        if (!rect) return;
        AutoDragModal.show({
            children: (
                <BorderRadiusInput
                    closeClick={() => {
                        AutoDragModal.close();
                    }}
                    borderRadius={borderRadius || DefaultBorderRadius}
                    onChange={(borderRadius: IBorderRadiusProps) => {
                        const data = activeLayer.data as IImageLayerProps;
                        data.borderRadius = borderRadius;
                        updateLayer(activeLayer);
                        window._stat_track?.('hb_layers_func_click', {
                            func_id: '2_162',
                        });
                        window._stat_track?.('hb_layers_func_use', {
                            func_id: '2_162',
                        });
                    }}
                />
            ),
            initPosition: {
                x: rect.x - 250 - 12,
                y: rect.y < 0 ? 0 : rect.y,
            },
            windowSize: {
                width: 250,
                height: 148,
            },
            className: 'c-image-effect-item-content-wrap',
            bgCanOperate: true,
        });
    };

    const updateRadius = (value: number) => {
        const data = activeLayer.data as IImageLayerProps;
        data.borderRadius = {
            leftBottom: value,
            leftTop: value,
            rightBottom: value,
            rightTop: value,
            hidden: !!data.borderRadius?.hidden,
        };
        updateLayer({ ...activeLayer });
    };

    return (
        <div className="c-image-effect-item-content" style={{ border: 'none' }} ref={elementRef}>
            <EffectItemShowTitle
                title={$t((v) => v.imageEffectPanel.borderRadiusItem.title)}
                iconName="UpperRightBold"
                handleVisibleClick={visibleClick}
                handleDelClick={delClick}
                visible={!!(borderRadius && !borderRadius.hidden)}
            />

            <div className="content">
                <LabelAction title={$t((v) => v.imageEffectPanel.borderRadiusItem.title)} align>
                    <div className="content-inner">
                        <div
                            style={{
                                width: '100%',
                                marginRight: '6px',
                            }}
                        >
                            <SliderInput
                                min={0}
                                max={100}
                                value={borderRadiusTotal}
                                precision={1}
                                showStepBtn
                                type="horizontal"
                                onChange={(val: number) => {
                                    AutoDragModal.close();
                                    updateRadius(val);
                                }}
                                onAfterChange={() => {
                                    window._stat_track?.('hb_layers_func_click', {
                                        func_id: '2_162',
                                    });
                                    window._stat_track?.('hb_layers_func_use', {
                                        func_id: '2_162',
                                    });
                                }}
                                onClick={() => {}}
                            />
                        </div>
                        <div onClick={showRadius} className="icon-wrap">
                            <IconFont
                                name="AmplificationBold"
                                fontSize={14}
                                color={`${
                                    (borderRadius &&
                                        areNumbersEqual(
                                            borderRadius.leftBottom,
                                            borderRadius.leftTop,
                                            borderRadius.rightBottom,
                                            borderRadius.rightTop,
                                        )) ||
                                    !borderRadius
                                        ? 'var(--content-btn-transparency)'
                                        : 'var(--content-system-link)'
                                }`}
                            />
                        </div>
                    </div>
                </LabelAction>
            </div>
        </div>
    );
};

export default ImageBorderRadioItem;
